import i18n from '@/i18n';
import { taskAPI } from '@/utils/api';
import { buildRetryTaskApiPatch } from './ipeTaskActions';
import { resolveImg2ImgReferenceForApi } from './img2imgPublicUrl';
import {
  getImageProcessStudioConfig,
  normalizeSmartCutoutFormats,
  normalizeSmartCutoutMode,
} from '@/pages/ipe/workbench/constants/imageProcessStudioConfig';

function readReferenceUrlsFromTaskResult(result) {
  const r = result && typeof result === 'object' ? result : {};
  const fromUrls = Array.isArray(r.referenceUrls) ? r.referenceUrls.filter(Boolean) : [];
  if (fromUrls.length > 0) return fromUrls;
  return Array.isArray(r.referencePreviews) ? r.referencePreviews.filter(Boolean) : [];
}

/** 智能抠图 / 超分 / 印花提取：历史重试改为重新入队服务端 Worker */
export async function retryImageProcessStudioTask(navId, task) {
  const config = getImageProcessStudioConfig(navId);
  if (!config) throw new Error(i18n.t('workbench:ipeUtilError.taskTypeInvalid'));

  const taskId = String(task?.id || '').trim();
  if (!taskId) throw new Error(i18n.t('workbench:ipeUtilError.taskInvalid'));

  const r = task?.result && typeof task.result === 'object' ? task.result : {};
  const rawRefs = readReferenceUrlsFromTaskResult(r);
  const operatorName = String(r.operatorName || r.operator || task?.operator || '').trim();
  const imageIds = Array.isArray(task.imageIds) ? task.imageIds : [];

  const imageUrls = [];
  for (const raw of rawRefs) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) continue;
    const resolved = await resolveImg2ImgReferenceForApi(trimmed);
    if (resolved) imageUrls.push(resolved);
  }
  if (imageUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskMissingSource'));
  }

  const estimatedTotal = Math.max(1, imageUrls.length);
  const initialResult = {
    referencePreviews: Array.isArray(r.referencePreviews) ? r.referencePreviews : rawRefs,
    referenceUrls: imageUrls,
    previewUrl: rawRefs[0] || imageUrls[0] || '',
    operator: operatorName,
    operatorName,
    engine: config.engine,
    failureHint: '',
    serverWorker: true,
    batchPhase: 'queued',
    outputs: [],
    urls: [],
    jobIds: {},
    ...(navId === 'smartCutout' ? {
      cutoutMode: normalizeSmartCutoutMode(r.cutoutMode),
      cutoutEngine: normalizeSmartCutoutMode(r.cutoutMode) === 'advanced' ? 'qwen' : 'aliyun',
      cutoutFormats: normalizeSmartCutoutFormats(r.cutoutFormats),
      outputFormat: 'png',
      localCutoutUrls: {},
      localFailedImageIds: [],
    } : {}),
    ...(navId === 'imageEnhance' ? {
      localEnhanceUrls: {},
      localFailedImageIds: [],
      engine: 'qwen-image-2.0',
      pipeline: 'qwen-clarity-v1',
    } : {}),
    ...(navId === 'stampExtract' ? {
      userPrompt: String(r.userPrompt || r.prompt || '').trim(),
      ...(String(r.userPrompt || r.prompt || '').trim()
        ? { prompt: String(r.userPrompt || r.prompt || '').trim() }
        : {}),
    } : {}),
  };

  await taskAPI.update(taskId, {
    ...buildRetryTaskApiPatch({
      total: estimatedTotal,
      successCount: 0,
      failedCount: 0,
    }),
    imageIds,
    result: initialResult,
  });
  await taskAPI.submit(taskId, { force: true });
  return { taskId };
}
